/**
 * Classification of errors and close events surfaced by the qz-tray
 * websocket. `QZErrorCode` is a stable enum that UI code can branch on
 * without parsing the raw English error strings thrown by `qz-tray.js`.
 */
export type QZErrorCode =
  | "alreadyOpen"
  | "browserUnsupported"
  | "cancelled"
  | "dropped"
  | "handshakeFailed"
  | "racing"
  | "unknown"
  | "unreachable";

export type QZCloseInfo = {
  code: null | number;
  reason: null | string;
  wasClean: boolean;
};

const UNREACHABLE_PATTERNS = [
  /unable to establish connection/i,
  /unable to create a websocket connection/i,
  /no ports have been specified/i,
];

const ALREADY_OPEN_PATTERN = /already exists/i;
const RACING_PATTERN =
  /connection attempt has not returned yet|waiting for previous disconnect/i;
const BROWSER_UNSUPPORTED_PATTERN =
  /websocket not supported|unsupported websocket version/i;
const CANCELLED_PATTERN = /cancelled by user/i;
const HANDSHAKE_PATTERN =
  /certificate|signature|signing|rejected by server|unsigned|untrusted|invalid request/i;

const messageOf = (err: unknown): string => {
  if (err instanceof Error) return err.message;

  if (typeof err === "string") return err;

  if (err && typeof err === "object" && "message" in err) {
    const value = (err as { message?: unknown }).message;
    return typeof value === "string" ? value : "";
  }
  return "";
};

/**
 * Classify an error thrown by `qz.websocket.connect` (or a later mutation)
 * into a `QZErrorCode`. `priorConnected` distinguishes an initial failure
 * ("unreachable") from a post-connect failure ("dropped" / "handshakeFailed").
 */
export const classifyConnectError = (
  err: unknown,
  priorConnected = false,
): QZErrorCode => {
  const message = messageOf(err);

  if (BROWSER_UNSUPPORTED_PATTERN.test(message)) return "browserUnsupported";

  if (ALREADY_OPEN_PATTERN.test(message)) return "alreadyOpen";

  if (RACING_PATTERN.test(message)) return "racing";

  if (CANCELLED_PATTERN.test(message)) return "cancelled";

  if (HANDSHAKE_PATTERN.test(message)) return "handshakeFailed";

  if (UNREACHABLE_PATTERNS.some((pattern) => pattern.test(message)))
    return priorConnected ? "dropped" : "unreachable";

  return "unknown";
};

/**
 * Classify a close event forwarded from `qz.websocket.setClosedCallbacks`.
 * Clean close (explicit disconnect, browser closing tab) is not an error.
 */
export const classifyCloseEvent = (info: QZCloseInfo): QZErrorCode | null => {
  if (info.wasClean) return null;

  return "dropped";
};

export const readCloseInfo = (event: unknown): QZCloseInfo => {
  if (event === null || typeof event !== "object")
    return { code: null, reason: null, wasClean: false };

  const record = event as Record<string, unknown>;
  const code = typeof record.code === "number" ? record.code : null;
  const reason = typeof record.reason === "string" ? record.reason : null;
  const wasClean = record.wasClean === true;

  return { code, reason, wasClean };
};
